import { useEffect, useState } from "react";

import { apiClient } from "../../api/client";
import type { BlockSeason, MeResponse } from "../../api/types";
import { hasPermission } from "../../auth/permissions";

// One key per user for every blocks page, so a season picked on one opens the
// others without leaking to another leader on the same browser.
function storageKey(me: MeResponse): string {
  return `blocks.season.${me.user.id}`;
}

// resolveBlockSeasons narrows the listed seasons to the page's statuses and
// picks the one it opens on: the remembered pick while it is still offered,
// else the active season, else the newest Planning one, else the newest closed
// one. The list arrives newest first. No seasons leaves seasonId "", which the
// server resolves to the active season.
export function resolveBlockSeasons(
  listed: BlockSeason[],
  remembered: string | null,
  statuses?: BlockSeason["status"][]
): { seasons: BlockSeason[]; seasonId: string } {
  const seasons = statuses === undefined ? listed : listed.filter((season) => statuses.includes(season.status));
  if (remembered !== null && seasons.some((season) => season.id === remembered)) {
    return { seasons, seasonId: remembered };
  }
  const newest = (status: BlockSeason["status"]) => seasons.find((season) => season.status === status);
  return { seasons, seasonId: (newest("active") ?? newest("draft") ?? newest("closed"))?.id ?? "" };
}

// useBlockSeason is the season a blocks page shows. Leadership
// (blocks:seasons:manage) gets every season to pick from, and ready stays false
// until the list is in, so the page never loads the active season first and
// then the resolved one. Everyone else gets no seasons and seasonId "" (the
// active season) straight away.
export function useBlockSeason(me: MeResponse, statuses?: BlockSeason["status"][]) {
  const canPick = hasPermission(me, "blocks:seasons:manage");
  const [state, setState] = useState<{ ready: boolean; seasons: BlockSeason[]; seasonId: string }>({
    ready: !canPick,
    seasons: [],
    seasonId: ""
  });

  useEffect(() => {
    if (!canPick) {
      return;
    }
    let cancelled = false;
    apiClient
      .listBlockSeasons({ limit: 50 })
      .then((res) => {
        if (!cancelled) {
          setState({ ready: true, ...resolveBlockSeasons(res.seasons, readRememberedSeason(me), statuses) });
        }
      })
      .catch(() => {
        // Without the list the page still works on the active season.
        if (!cancelled) {
          setState({ ready: true, seasons: [], seasonId: "" });
        }
      });
    return () => {
      cancelled = true;
    };
    // statuses is left out: callers pass an inline array, which would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPick]);

  function setSeasonId(seasonId: string) {
    setState((previous) => ({ ...previous, seasonId }));
    try {
      window.localStorage.setItem(storageKey(me), seasonId);
    } catch {
      // Without storage the pick still holds on this page.
    }
  }

  return { ...state, season: state.seasons.find((season) => season.id === state.seasonId), setSeasonId };
}

function readRememberedSeason(me: MeResponse): string | null {
  try {
    return window.localStorage.getItem(storageKey(me));
  } catch {
    return null;
  }
}
